import React, { useState } from 'react';
import { BarChart3, ExternalLink, FileText, Images, Newspaper, RefreshCcw } from 'lucide-react';

function MediaFallback({ postUrl, t }) {
  return (
    <div className="flex min-h-32 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#C9D0DA] bg-[#F7F8F6] p-4 text-center text-sm text-[#667085]">
      <RefreshCcw className="h-5 w-5" />
      <span>{t('Media preview is unavailable or has expired.')}</span>
      {postUrl && <a href={postUrl} target="_blank" rel="noreferrer" className="font-semibold text-[#0B1D3A] underline">{t('Open on LinkedIn')}</a>}
    </div>
  );
}

function MediaItem({ item, postUrl, t }) {
  const [failed, setFailed] = useState(Boolean(item.unavailable));
  if (failed || !item.url) return <MediaFallback postUrl={postUrl} t={t} />;
  if (item.type === 'video') {
    return (
      <video controls preload="metadata" poster={item.thumbnail_url || undefined} onError={() => setFailed(true)} className="max-h-[520px] w-full rounded-xl bg-black object-contain">
        <source src={item.url} />
      </video>
    );
  }
  if (item.type === 'document') {
    return (
      <a href={item.url} target="_blank" rel="noreferrer" className="flex min-h-32 items-center gap-3 rounded-xl border border-[#D7DCE4] bg-[#F8FAFA] p-4 hover:border-[#12C8C6]">
        <FileText className="h-8 w-8 shrink-0 text-[#12A9A8]" />
        <span className="min-w-0"><strong className="block truncate text-sm text-[#0B1D3A]">{item.title || t('LinkedIn document')}</strong><span className="text-xs text-[#667085]">{t('Open document')}</span></span>
        <ExternalLink className="ml-auto h-4 w-4 shrink-0 text-[#667085]" />
      </a>
    );
  }
  return <img src={item.url} alt="" loading="lazy" onError={() => setFailed(true)} className="max-h-[520px] w-full rounded-xl bg-[#F5F6F7] object-contain" />;
}

export default function EngagementMedia({ post, t }) {
  const media = post.media || [];
  const poll = post.poll;
  const article = post.article;
  const repost = post.repost;
  return (
    <div className="mt-4 space-y-3">
      {media.length > 0 && (
        <div className={`grid gap-2 ${media.length > 1 ? 'sm:grid-cols-2' : ''}`}>
          {media.length > 1 && <span className="col-span-full inline-flex items-center gap-1 text-xs font-semibold text-[#667085]"><Images className="h-3.5 w-3.5" />{media.length} {t('media items')}</span>}
          {media.map((item, index) => <MediaItem key={`${item.url || item.title}-${index}`} item={item} postUrl={post.post_url} t={t} />)}
        </div>
      )}
      {poll && (
        <section className="rounded-xl border border-[#D7DCE4] bg-[#F8FAFA] p-4">
          <p className="flex items-center gap-2 font-semibold text-[#0B1D3A]"><BarChart3 className="h-4 w-4 text-[#12A9A8]" />{poll.question || t('LinkedIn poll')}</p>
          <div className="mt-3 space-y-2">{(poll.options || []).map((option, index) => (
            <div key={`${option.text}-${index}`} className="relative overflow-hidden rounded-lg border border-[#D7DCE4] bg-white px-3 py-2 text-sm text-[#344054]">
              {Number.isFinite(option.percentage) && <span className="absolute inset-y-0 left-0 bg-[#DDF8F7]" style={{ width: `${Math.max(0, Math.min(100, option.percentage))}%` }} />}
              <span className="relative flex justify-between gap-2"><span>{option.text}</span>{option.votes > 0 && <span className="text-xs text-[#667085]">{option.votes}</span>}</span>
            </div>
          ))}</div>
          <p className="mt-2 text-xs text-[#667085]">{t('Read-only poll')}</p>
        </section>
      )}
      {article && (
        <a href={article.url} target="_blank" rel="noreferrer" className="flex overflow-hidden rounded-xl border border-[#D7DCE4] bg-white hover:border-[#12C8C6]">
          {article.thumbnail_url && <img src={article.thumbnail_url} alt="" className="h-28 w-32 shrink-0 object-cover" />}
          <span className="min-w-0 p-3"><span className="flex items-center gap-1 text-xs font-semibold uppercase text-[#667085]"><Newspaper className="h-3.5 w-3.5" />{t('Article')}</span><strong className="mt-1 block text-sm text-[#0B1D3A]">{article.title || article.url}</strong>{article.description && <span className="mt-1 line-clamp-2 block text-xs text-[#667085]">{article.description}</span>}</span>
        </a>
      )}
      {repost && (
        <section className="rounded-xl border-l-4 border-[#12C8C6] bg-[#F4FBFA] p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-[#667085]">{t('Reposted from')} {repost.author_name || t('LinkedIn member')}</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#344054]">{repost.content || t('No post text available.')}</p>
          {(repost.media || []).length > 0 && <div className="mt-3 grid gap-2 sm:grid-cols-2">{repost.media.map((item, index) => <MediaItem key={`${item.url}-${index}`} item={item} postUrl={repost.post_url || post.post_url} t={t} />)}</div>}
        </section>
      )}
    </div>
  );
}
